import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { HandCoins, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { createSadaqahEntry, getLocalDateString, getSadaqahEntries, parseLocalDateKey } from '../lib/api';

export default function Sadaqah({ user }) {
  const { t, i18n } = useTranslation();
  const isHr = i18n.language === 'hr';
  const [sadaqahList, setSadaqahList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    date: getLocalDateString(),
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchSadaqah();
  }, []);

  const fetchSadaqah = async () => {
    setErrorMessage('');
    try {
      const data = await getSadaqahEntries();
      setSadaqahList(data);
    } catch (error) {
      console.error('Error fetching sadaqah:', error);
      setSadaqahList([]);
      setErrorMessage(isHr ? 'Učitavanje sadake nije uspjelo.' : 'Failed to load sadaqah entries.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      await createSadaqahEntry({
        amount: Number(formData.amount),
        description: formData.description,
        date: formData.date,
      });
      setShowModal(false);
      setFormData({
        amount: '',
        description: '',
        date: getLocalDateString(),
      });
      toast.success(isHr ? 'Sadaka je spremljena.' : 'Sadaqah entry saved.');
      fetchSadaqah();
    } catch (error) {
      console.error('Error creating sadaqah:', error);
      const message = isHr ? 'Spremanje sadake nije uspjelo.' : 'Failed to save sadaqah entry.';
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = sadaqahList.reduce((sum, s) => sum + Number(s.amount || 0), 0);

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto px-6 py-8 md:py-12">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#292524] mb-2">
              {t('sadaqah_tracker')}
            </h1>
            <p className="text-[#57534E] font-body">
              Record your charity and giving
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            data-testid="add-sadaqah-button"
            className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-all active:scale-95 shadow-md"
          >
            <Plus className="w-5 h-5" />
            {t('add')}
          </button>
        </div>

        <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-6 shadow-card mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
              <HandCoins className="w-6 h-6 text-accent" />
            </div>
            <div>
              <p className="text-sm text-[#57534E]">{t('total')} Sadaqah</p>
              <p className="text-3xl font-heading font-bold text-primary" data-testid="total-sadaqah-amount">
                ${totalAmount.toFixed(2)}
              </p>
            </div>
          </div>
          <p className="text-sm text-[#57534E] font-body">
            "The example of those who spend their wealth in the way of Allah is
            like a seed of grain" - Quran 2:261
          </p>
        </div>

        {errorMessage && (
          <div
            className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            data-testid="sadaqah-error-message"
          >
            {errorMessage}
          </div>
        )}

        <div className="space-y-3">
          {sadaqahList.length === 0 ? (
            <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-xl p-12 text-center" data-testid="sadaqah-empty-state">
              <HandCoins className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
              <p className="text-[#57534E] font-body">No sadaqah recorded yet</p>
              <p className="text-sm text-[#A8A29E] mt-2">
                Start tracking your charity
              </p>
            </div>
          ) : (
            sadaqahList.map((sadaqah) => (
              <div
                key={sadaqah.id}
                className="bg-white border border-stone-200/60 rounded-xl p-4 shadow-sm hover:shadow-md transition-all"
                data-testid={`sadaqah-item-${sadaqah.id}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-[#292524] mb-1">
                      {sadaqah.description}
                    </p>
                    <p className="text-sm text-[#57534E]">
                      {parseLocalDateKey(sadaqah.date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-heading font-semibold text-primary">
                      ${Number(sadaqah.amount || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-heading font-semibold text-[#292524]">
                {t('add_sadaqah')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                data-testid="close-sadaqah-modal"
                className="p-2 hover:bg-stone-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {t('amount')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) =>
                    setFormData({ ...formData, amount: e.target.value })
                  }
                  data-testid="sadaqah-amount-input"
                  className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {t('description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  data-testid="sadaqah-description-input"
                  className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  rows="3"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {t('date')}
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                  data-testid="sadaqah-date-input"
                  className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="sadaqah-submit-button"
                className="w-full py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('loading') : t('save')}
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}